import React, { useEffect, useState } from "react";
import schoolApi from "../../api/schoolApi";
import "./FeeReceipt.css";
import "react-toastify/dist/ReactToastify.css";
import { Col, Row } from "react-bootstrap";
import jwt_decode from "jwt-decode";
import { buildReceiptRows } from "./receiptCalc";
const parseSafe = (val) => {
  if (typeof val === "string") return parseFloat(val.replace(/,/g, "")) || 0;
  return parseFloat(val) || 0;
};
/**
 * `api` (optional): data source used instead of the logged-in API – the public share page
 * (/view/<token>) passes one built from the share-link payload, so the receipt looks exactly the same.
 * { tenantcode, fetchFeeDepositsByStudentId, getallIntsallment, getSessionById }
 */
const FeeReceipt = React.forwardRef(({ receiptData, onReady, api }, receiptRefs) => {
  const [studentRecord, setStudentRecord] = useState();
  const [headRecords, setHeadRecords] = useState([]);
  const [depoMonths, setMonths] = useState([]);
  const [depositRecords, setdepositRecords] = useState([]);
  const [allMasterInstallments, setAllMasterInstallments] = useState([]);
  const [filterMonths, setFilterMonths] = useState([]);
  const [tenantCode, setTenantCode] = useState(null);
  const [overallPaidTotal, setOverallPaidTotal] = useState(0);
  const [receiptTotals, setReceiptTotals] = useState(null);

  useEffect(() => {
    if (receiptData) {
      setStudentRecord(null);
      setHeadRecords([]);
      setMonths([]);
      setdepositRecords([]);
      setAllMasterInstallments([]);
      setFilterMonths([]);
      setOverallPaidTotal(0);
      setReceiptTotals(null);

      async function init() {
        try {
          const src = api || schoolApi;
          if (api?.tenantcode) {
            setTenantCode(api.tenantcode);
          } else {
            const user = jwt_decode(sessionStorage.getItem("token"));
            setTenantCode(user.tenantcode);
          }
          let session = receiptData?.sessionid
            ? receiptData?.sessionid
            : receiptData?.session_id;

          // 1. First fetch the CURRENT receipt to get reliable session ID & deposit data
          const result = await src.fetchFeeDepositsByStudentId(
            receiptData?.student_addmission_id,
            null,
            receiptData?.deposit_id
          );

          if (!result || result.success === false) {
            return;
          }

          let depoRecordsResult = result?.result?.[0] || result?.[0];
          if (depoRecordsResult && Array.isArray(depoRecordsResult.result) && depoRecordsResult.result[0]) {
            depoRecordsResult = depoRecordsResult.result[0];
          }
          const sessionToUse = depoRecordsResult?.session_id || depoRecordsResult?.sessionid || session;

          // 2. Fetch ALL receipts for this student in this session for chronological history
          const allReceiptsResult = await src.fetchFeeDepositsByStudentId(
            receiptData?.student_addmission_id,
            sessionToUse
          );

          let rawReceipts = allReceiptsResult?.result || allReceiptsResult || [];
          if (Array.isArray(rawReceipts) && rawReceipts[0] && Array.isArray(rawReceipts[0].result)) {
            rawReceipts = rawReceipts[0].result;
          }
          const allReceipts = (Array.isArray(rawReceipts) ? rawReceipts : []).filter(
            (r) => r && r.status !== "cancelled"
          );
          const depoRecords = depoRecordsResult;

          const allInstallmentsResult = await src.getallIntsallment(
            receiptData?.student_addmission_id,
            sessionToUse
          );

          const masterInstallments = allInstallmentsResult?.result || allInstallmentsResult || [];
          setAllMasterInstallments(masterInstallments);

          const getFirstReceiptNum = (str) => {
            if (!str) return 0;
            const match = String(str).match(/\d+/);
            return match ? parseInt(match[0]) : 0;
          };

          const currentReceiptNum = getFirstReceiptNum(depoRecords?.receipt_number);
          const currentDepositId = depoRecords?.deposit_id || depoRecords?.id;
          const currentCreatedTime = depoRecords?.createddate ? new Date(depoRecords.createddate).getTime() : 0;

          // Helper to check if receipt 'r' occurred strictly prior to current receipt
          const isStrictlyPrior = (r) => {
            const rId = r.deposit_id || r.id;
            if (rId === currentDepositId) return false;
            const rCreatedTime = r.createddate ? new Date(r.createddate).getTime() : 0;
            if (rCreatedTime && currentCreatedTime && rCreatedTime !== currentCreatedTime) {
              return rCreatedTime < currentCreatedTime;
            }
            const rNum = getFirstReceiptNum(r.receipt_number);
            return rNum < currentReceiptNum;
          };

          // Helper to check if receipt 'r' occurred up to and including the current receipt
          const isUpToCurrent = (r) => {
            const rId = r.deposit_id || r.id;
            if (rId === currentDepositId) return true;
            return isStrictlyPrior(r);
          };

          const priorReceipts = allReceipts.filter(isStrictlyPrior);
          const receiptsUpToCurrent = allReceipts.filter(isUpToCurrent);

          // Calculate Total Fee Paid (Overall) = sum of all receipts up to and including this one
          const overallPaid = receiptsUpToCurrent.reduce((acc, r) => acc + parseSafe(r.amount || 0), 0);
          setOverallPaidTotal(overallPaid);

          const current_session = await src.getSessionById(depoRecords?.session_id || depoRecords?.sessionid);
          const filteredMonthsName = getMonthNamesBetweenDates(
            current_session?.result?.startdate,
            current_session?.result?.enddate
          );
          setFilterMonths(filteredMonthsName);

          // keep an untouched copy – used when the month filter below removes everything
          const originalInstallments = [...(depoRecords?.installments || [])];

          // Filter and sort installments of this deposit
          if (depoRecords?.installments?.length > 0) {
            depoRecords.installments = depoRecords?.installments
              .map((installment) => {
            const notmonthlytype = (installment.month || "").split(" to ");
                if (notmonthlytype.length === 1) {
                  return filteredMonthsName.includes(installment.month) ? installment : null;
                } else {
                  const [startMonth, endMonth] = notmonthlytype.map((m) => m.trim());
                  const startIndex = filteredMonthsName.indexOf(startMonth);
                  const endIndex = filteredMonthsName.indexOf(endMonth);
                  if (startIndex !== -1 && endIndex !== -1) {
                    const monthsInRange = filteredMonthsName.slice(startIndex, endIndex + 1);
                    return { ...installment, expandedMonths: monthsInRange };
                  } else {
                    return null;
                  }
                }
              })
              .filter(Boolean);

            depoRecords.installments.sort((a, b) => {
              const aFirstMonth = Array.isArray(a.expandedMonths) ? a.expandedMonths[0] : a.month;
              const bFirstMonth = Array.isArray(b.expandedMonths) ? b.expandedMonths[0] : b.month;
              return filteredMonthsName.indexOf(aFirstMonth) - filteredMonthsName.indexOf(bFirstMonth);
            });
          }

          // Sort prior receipts chronologically
          const sortedPriorReceipts = [...priorReceipts].sort((a, b) => {
            const aTime = a.createddate ? new Date(a.createddate).getTime() : 0;
            const bTime = b.createddate ? new Date(b.createddate).getTime() : 0;
            if (aTime && bTime && aTime !== bTime) return aTime - bTime;
            return getFirstReceiptNum(a.receipt_number) - getFirstReceiptNum(b.receipt_number);
          });

          // ---- make sure the receipt knows every installment it paid, with its fee heads ----
          const norm = (s) => String(s || "").trim().toLowerCase();
          const findMaster = (inst) =>
            (masterInstallments || []).find((m) => m.id === inst.id) ||
            (masterInstallments || []).find((m) => norm(m.month) === norm(inst.month));
          const instById = new Map();
          ((depoRecords?.installments || []).length ? depoRecords.installments : originalInstallments)
            .forEach((i) => instById.set(i.id, i));
          (depoRecords?.payment_breakdown || []).forEach((pb) => {
            if (!instById.has(pb.installment_id)) {
              const m = (masterInstallments || []).find((x) => x.id === pb.installment_id)
                || originalInstallments.find((x) => x.id === pb.installment_id);
              if (m) instById.set(pb.installment_id, m);
            }
          });
          depoRecords.installments = Array.from(instById.values())
            .map((inst) => {
              if ((inst.heads || []).length) return inst;
              const m = findMaster(inst);
              if (m && (m.heads || []).length) {
                return {
                  ...inst,
                  heads: m.heads,
                  installment_amount: inst.installment_amount ?? m.installment_amount,
                  installment_discount: inst.installment_discount ?? m.installment_discount,
                  transport_fee: inst.transport_fee ?? m.transport_fee,
                };
              }
              return inst;
            })
            .sort((a, b) => {
              const ao = parseSafe(a.orderno), bo = parseSafe(b.orderno);
              if (ao !== bo) return ao - bo;
              const am = String(a.month || "").split(" to ")[0].trim();
              const bm = String(b.month || "").split(" to ")[0].trim();
              return filteredMonthsName.indexOf(am) - filteredMonthsName.indexOf(bm);
            });
          if (!depoRecords.installments.length) {
            console.warn("FeeReceipt: no installment found for this receipt", depoRecords?.deposit_id);
          }

          // Rows come from stored data only: line items (fee paid per installment),
          // the receipt's own late fee and each installment's discount. See receiptCalc.js
          const { rows, totals } = buildReceiptRows(depoRecords, sortedPriorReceipts);
          const months = [];
          (depoRecords?.installments || []).forEach((inst) => {
            if (!months.some((m) => m.month === inst.month)) months.push({ month: inst.month });
          });
          const headArray = rows;
          setReceiptTotals(totals);

          setMonths(months);
          setStudentRecord(depoRecords);
          setHeadRecords(headArray.filter((res) => res.head_amount > 0));
        } catch (error) {
          console.error("Error in FeeReceipt init:", error);
        }
      }
      init();
    }
  }, [receiptData, api]);

  useEffect(() => {
    if (studentRecord && onReady) {
      onReady();
    }
  }, [studentRecord, onReady]);

  function getMonthNamesBetweenDates(start, end) {
    const startDate = new Date(start);
    const endDate = new Date(end);
    const monthNames = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];

    const result = [];
    let currentDate = new Date(startDate);

    while (currentDate <= endDate) {
      const monthName = monthNames[currentDate.getMonth()];
      if (!result.includes(monthName)) {
        result.push(monthName);
      }
      currentDate.setMonth(currentDate.getMonth() + 1);
    }

    return result;
  }

  const convertNumberToWords = (num) => {
    if (num === null || num === undefined || num === "" || isNaN(Number(num))) {
      return "Zero";
    }
    const val = Math.floor(Math.abs(Number(num)));
    if (val === 0) return "Zero";
    const a = [
      "",
      "One",
      "Two",
      "Three",
      "Four",
      "Five",
      "Six",
      "Seven",
      "Eight",
      "Nine",
      "Ten",
      "Eleven",
      "Twelve",
      "Thirteen",
      "Fourteen",
      "Fifteen",
      "Sixteen",
      "Seventeen",
      "Eighteen",
      "Nineteen",
    ];
    const b = [
      "",
      "",
      "Twenty",
      "Thirty",
      "Forty",
      "Fifty",
      "Sixty",
      "Seventy",
      "Eighty",
      "Ninety",
    ];
    const g = ["", "Thousand", "Million", "Billion", "Trillion"];
    let s = val.toString();
    if (s.length > 15) return "too large";
    let n = ("000000000000000" + s).substr(-15).match(/.{3}/g);
    let str = "";
    for (let i = 0; i < n.length; i++) {
      let hundreds = parseInt(n[i][0]);
      let tens = parseInt(n[i][1]);
      let units = parseInt(n[i][2]);
      let word = "";
      if (hundreds > 0) {
        word += a[hundreds] + " hundred ";
      }
      if (tens > 1) {
        word += b[tens] + " " + a[units];
      } else {
        word += a[10 * tens + units];
      }
      if (word.trim()) {
        str += word + " " + g[n.length - 1 - i] + " ";
      }
    }
    return str.replace(/\s+/g, " ").trim();
  };

  const totalFees = receiptTotals ? receiptTotals.totalDue : headRecords?.reduce((acc, item) => acc + parseSafe(item?.head_amount), 0);
  const totalConcessionAmount = receiptTotals ? receiptTotals.totalConcession : headRecords?.reduce((acc, item) => acc + parseSafe(item?.displayDiscount || 0), 0);
  const netPayable = Math.max(0, totalFees - totalConcessionAmount);
  const currentPaid = parseSafe(studentRecord?.amount || 0);
  const receiptBalance = receiptTotals ? receiptTotals.balance : Math.max(0, netPayable - currentPaid);



  return (
    <>
      <div className="fee-receipt-container-receipt">
        {studentRecord ? (
          <div
            id="reportContent"
            ref={receiptRefs}
            className="fee-receipt-receipt"
          >
            {["Office Copy", "Parent Copy"].map((copyType, idx) => (
              <React.Fragment key={idx}>
                <div className="receipt-copy-receipt" data-copy={copyType}>
                  <header className="header-receipt">
                    <div className="receipt-header-row">
                      <div className="receipt-header-logo">
                        <img
                          src={`/${tenantCode}-sidebar-logo.png`}
                          alt=""
                          onError={(e) => {
                            e.currentTarget.style.display = "none";
                          }}
                        />
                      </div>
                      <div className="receipt-header-school">
                        <h1>{studentRecord.school_name}</h1>
                        <p>
                          {studentRecord.school_address}
                          {studentRecord.state ? `, ${studentRecord.state}` : ""}
                          {studentRecord.country ? `, ${studentRecord.country}` : ""}
                          {studentRecord.pincode ? ` - ${studentRecord.pincode}` : ""}
                        </p>
                      </div>
                      <div className="receipt-header-copy">
                        <span className="copy-badge">{copyType}</span>
                      </div>
                    </div>
                  </header>

                  <div className="receipt-title">
                    Fee Receipt ({studentRecord.session})
                  </div>
                { }
                <div className="receipt-info-receipt">
                  <div className="info-left-receipt">
                    <p>
                      <strong>Receipt No :</strong>{" "}
                      {studentRecord.receipt_number}
                    </p>
                    <p>
                      <strong>Student Name :</strong>{" "}
                      {studentRecord.studentname}
                    </p>
                    <p>
                      <strong>Registration No :</strong>{" "}
                      {studentRecord.registration_id}
                    </p>
                    <p>
                      <strong>Mobile No :</strong> {studentRecord.phone}
                    </p>
                  </div>
                  <div className="info-right-receipt">
                    <p>
                      <strong>Receipt Date :</strong>{" "}
                      {studentRecord.payment_date}
                    </p>
                    <p>
                      <strong>Parent's Name :</strong>{" "}
                      {studentRecord.parent_name}
                    </p>
                    <p>
                      <strong>Class :</strong> {studentRecord.classname} (
                      {studentRecord.sectionname})
                    </p>
                    <p>
                      <strong>Parent's Contact :</strong>{" "}
                      {studentRecord.parentphone}
                    </p>
                    <p>
                      <strong>Total Fee Paid (Overall) :</strong>{" "}
                      {overallPaidTotal}
                    </p>
                  </div>
                </div>
                <div className="receipt-table-wrapper">
                  <table className="table-receipt">
                    <thead>
                      <tr>
                        <th>S. No.</th>
                        <th>Particulars</th>
                        <th>Due Month</th>
                        <th>Due Amount</th>
                        {totalConcessionAmount > 0 && <th>Concession Amount</th>}
                        <th>Paid Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {headRecords.map((item, index) => (
                        <tr key={index}>
                          <td className="text-center">{index + 1}</td>
                          <td>{item.headname}</td>
                          <td className="text-center">
                            {(Array.isArray(item.month) ? item.month : []).map((month, idx) => (
                              <div key={idx}>{month}</div>
                            ))}
                          </td>
                          <td className="amount text-right">{item.head_amount}</td>
                          {totalConcessionAmount > 0 && (
                            <td className="amount text-right">{item.displayDiscount || 0}</td>
                          )}
                          <td className="amount text-right">{item.paidInThisReceipt}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colSpan={totalConcessionAmount > 0 ? "5" : "4"} className="text-right">
                          <strong>Total Amount (Gross) (in Rs):</strong>
                        </td>
                        <td className="amount text-right">
                          <strong>{totalFees}</strong>
                        </td>
                      </tr>
                      {totalConcessionAmount > 0 && (
                        <tr>
                          <td colSpan="5" className="text-right">
                            <strong>Total Concession (in Rs):</strong>
                          </td>
                          <td className="amount text-right">
                            <strong>{totalConcessionAmount}</strong>
                          </td>
                        </tr>
                      )}
                      {totalConcessionAmount > 0 && (
                        <tr style={{ backgroundColor: '#f0f0f0' }}>
                          <td colSpan="5" className="text-right">
                            <strong>Net Payable Amount (in Rs):</strong>
                          </td>
                          <td className="amount text-right">
                            <strong>{totalFees - totalConcessionAmount}</strong>
                          </td>
                        </tr>
                      )}
                      <tr style={{ backgroundColor: '#f9f9f9' }}>
                        <td colSpan={totalConcessionAmount > 0 ? "5" : "4"} className="text-right">
                          <strong>Current Paid (in Rs):</strong>
                        </td>
                        <td className="amount">
                          <strong>{studentRecord.amount}</strong>
                        </td>
                      </tr>
                      <tr>
                        <td colSpan={totalConcessionAmount > 0 ? "5" : "4"} className="text-right">
                          <strong>Due Amount (Balance):</strong>
                        </td>
                        <td className="amount">
                          <strong>
                            {receiptBalance}
                          </strong>
                        </td>
                      </tr>
                      <tr>
                        <td colSpan={totalConcessionAmount > 0 ? "6" : "5"} className="text-right">
                          <strong className="text-capitalize">
                            Amount In Words: Rupees{" "}
                            {convertNumberToWords(studentRecord.amount)} Only.
                          </strong>
                        </td>
                      </tr>
                      <tr>
                        <td colSpan={totalConcessionAmount > 0 ? "6" : "5"} className="text-right">
                          <strong>Payment Mode:</strong>{" "}
                          {studentRecord.payment_method} |
                          {studentRecord.transaction_no ? (
                            <>
                              <strong>Cheque No. / Transaction Id:</strong>{" "}
                              {studentRecord.transaction_no} |
                            </>
                          ) : null}
                          <strong>Cheque Date / Transaction Date:</strong>{" "}
                          {studentRecord.transaction_date}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>

                <div className="remarks-receipt">
                  <p>
                    <strong>Remark:</strong> {studentRecord.remark}
                  </p>
                </div>
                <div className="footer-receipt">
                  <p>
                    Generated by {studentRecord.generated_by} on{" "}
                    {studentRecord.generated_date}
                  </p>
                  <p>
                    <strong>NOTE:</strong> FEE IS NOT REFUNDABLE (EXCEPT CAUTION
                    FEE)
                  </p>
                </div>
              </div>
              {idx === 0 && <div className="dotted-line" key={`sep-${idx}`}></div>}
            </React.Fragment>
          ))}
        </div>
      ) : (
        <p>No Receipt Found</p>
      )}
    </div>
  </>
  );
});

export default FeeReceipt;
